import { AbstractControl, FormArray, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import { isOverlapping, isSameOrAfter, isWithin } from '@ftr/contracts/shared/TimeframeWithLocalTimesUtils'
import { DateFormat, toMomentAndFormat } from '@ftr/foundation'
import { TimeRangeControl, TimeRangeControlFormValue, isFilledOut, isTimeRangeControl } from './time-range-control'

/**
 * With optional ranges, it is invalid to only supply start without end.
 *
 * With required ranges, the inputs themselves are required so we dont have this issue
 */
export function allOrNothing(inputValidation: ValidatorFn[] = []): ValidatorFn {
  return (control: AbstractControl) => {
    if (isTimeRangeControl(control)) {
      const isRequired = inputValidation.some(validator => validator === Validators.required)

      if (!isRequired && !!control.controls.start.value !== !!control.controls.end.value) {
        return {
          allOrNothing: 'Both start time and end time must be specified.',
        }
      }
    }

    return null
  }
}

export function isStartAfterEnd(): ValidatorFn {
  return (control: AbstractControl) => {
    if (isTimeRangeControl(control)) {
      if (!control.controls.start.value || !control.controls.end.value) {
        return null
      }

      if (isSameOrAfter(control.controls.start.value, control.controls.end.value)) {
        return {
          startAfterEnd: 'The end time must be after the start time.',
        }
      }
    }

    return null
  }
}

export function isWithinTimeRange(scheduleControl: TimeRangeControl): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const self = control as TimeRangeControl
    const scheduleRawValue = scheduleControl.getRawValue()

    if (
      isFilledOut(scheduleRawValue) &&
      isFilledOut(self.getRawValue()) &&
      !isWithinOptional(self.getRawValue(), scheduleRawValue)
    ) {
      const startTimeString = toMomentAndFormat(scheduleControl.value!.start!, { format: DateFormat.Time })
      const endTimeString = toMomentAndFormat(scheduleControl.value!.end!, { format: DateFormat.Time })
      return {
        overlapping: `Session times cannot be outside recording times, this session should be within ${startTimeString} - ${endTimeString}`,
      }
    }
    return null
  }
}

export function isNotOverlappingTimeRange(
  formArray: FormArray<TimeRangeControl>,
  errorMessage: string = 'This time range overlaps with',
  timeFormat: DateFormat = DateFormat.Time,
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null
    }

    const self = control as TimeRangeControl

    const allControls: TimeRangeControl[] = formArray.controls
    const withoutSelf = allControls.filter(x => x !== control)
    const overlapping = withoutSelf.find(c => isOverlappingOptional(self.getRawValue(), c.getRawValue()))

    if (overlapping) {
      const startTimeString = toMomentAndFormat(overlapping.value!.start!, { format: timeFormat })
      const endTimeString = toMomentAndFormat(overlapping.value!.end!, { format: timeFormat })
      return {
        overlapping: `${errorMessage} ${startTimeString} - ${endTimeString}`,
      }
    }

    return null
  }
}

export function isScheduleFilled(scheduleControl: TimeRangeControl): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.value) {
      return null
    }
    const self = control as TimeRangeControl
    const scheduleRawValue = scheduleControl.getRawValue()
    if (!isFilledOut(scheduleRawValue) && isFilledOut(self.getRawValue())) {
      return {
        overlapping: 'Cannot have session times without a recording schedule',
      }
    }
    return null
  }
}

export function isWithinOptional(
  timeframe1: Partial<TimeRangeControlFormValue>,
  timeframe2: Partial<TimeRangeControlFormValue>,
): boolean {
  return isFilledOut(timeframe1) && isFilledOut(timeframe2) && isWithin(timeframe1, timeframe2)
}

export function isOverlappingOptional(
  timeframe1: Partial<TimeRangeControlFormValue>,
  timeframe2: Partial<TimeRangeControlFormValue>,
): boolean {
  return isFilledOut(timeframe1) && isFilledOut(timeframe2) && isOverlapping(timeframe1, timeframe2)
}
